// Разбор денег, введённых человеком. Обратная сторона `format.ts`: там копейки становятся
// строкой для глаза, здесь строка из поля становится целыми копейками для сервера.
//
// Место одно на всё приложение по той же причине, что и формат: пять разных разборов дают
// пять разных сумм в отчёте одного человека. Считаем по строке, БЕЗ плавающей точки:
// `Math.round(85000.7 * 100)` на длинных суммах теряет копейку, а это зарплата.

import type { Kopecks } from '@/shared/api/types';
import { kopecks } from '@/shared/api/types';

/** Что не так с введённой суммой — словами, для подписи под полем (правило 10). */
export type MoneyParseError = 'empty' | 'not_a_number' | 'too_many_fraction_digits' | 'too_large';

export type MoneyParseResult =
  { ok: true; value: Kopecks } | { ok: false; reason: MoneyParseError };

/** Всё, чем человек разделяет разряды: пробел обычный, неразрывный, узкий и апостроф. */
const SEPARATORS = /[\s\u00A0\u202F']/g;

/**
 * «85 000» → 8 500 000 копеек, «85000,50» → 8 500 050, «1 234.5» → 123 450.
 *
 * Запятая и точка равноправны: половина людей приходит из Excel с запятой, половина с
 * телефонной клавиатуры с точкой. Отказать за разделитель — значит потратить человеку
 * попытку на то, что мы и так понимаем.
 */
export function parseRublesToKopecks(input: string): MoneyParseResult {
  const cleaned = input.replace(SEPARATORS, '').replace(',', '.');
  if (cleaned === '') {
    return { ok: false, reason: 'empty' };
  }
  const match = /^(\d+)(?:\.(\d*))?$/.exec(cleaned);
  if (match === null) {
    return { ok: false, reason: 'not_a_number' };
  }
  const whole = match[1] ?? '0';
  const fraction = match[2] ?? '';
  if (fraction.length > 2) {
    return { ok: false, reason: 'too_many_fraction_digits' };
  }
  const total = Number(`${whole}${fraction.padEnd(2, '0')}`);
  if (!Number.isSafeInteger(total)) {
    return { ok: false, reason: 'too_large' };
  }
  // Копейки округляются до рубля ПРЯМО НА ВВОДЕ, половина вверх: 1454,50 → 1455.
  //
  // С 10.09.2026 в системе копеек нет вовсе (решение владельца со слов заказчика: деньги
  // выдают наличными, разменять копейку нечем). Движок считает целыми рублями, показ
  // округляет — но обе линии бесполезны, если дробную сумму можно завести руками: одна
  // заявка на 1 200,50, и копейка поехала в остаток подотчёта, в ведомость и в печать.
  //
  // Округляем, а не отказываем: человеку с чеком на 1 200,50 надо чем-то отчитаться, и
  // «введите целое» оставляет его без ответа, что делать с полтиной.
  return { ok: true, value: kopecks(Math.floor((total + 50) / 100) * 100) };
}

/** Текст ошибки для подписи под полем: что не так и что сделать. */
export function moneyErrorText(reason: MoneyParseError, subject: string): string {
  switch (reason) {
    case 'empty':
      return `Укажите ${subject} — без неё тариф не посчитать.`;
    case 'not_a_number':
      return `${capitalize(subject)} пишется цифрами: 85 000. Копейки округлятся до рубля.`;
    case 'too_many_fraction_digits':
      return 'После запятой не больше двух цифр. Копейки всё равно округлятся до рубля.';
    case 'too_large':
      return 'Сумма слишком велика — проверьте, не лишний ли ноль.';
  }
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
